import { HttpContext, HttpContextToken, HttpRequest } from '@angular/common/http';

/**
 * ACCEPT_LANGUAGE — a request-scoped marker carrying the /v2 current language.
 *
 * /v2's HTTP repositories tag their requests with it (via {@link withLanguage}).
 * The GLOBAL `headersInterceptor` reads it and, WHEN PRESENT, sets the
 * `Accept-Language` header to this value instead of v1's language source. v1
 * requests never set it, so their behaviour is byte-for-byte unchanged — which
 * keeps /v2 fully isolated while still letting every v2 API call carry the v2
 * language the user selected.
 *
 * Default `null` ⇒ "this is not a /v2 request".
 */
export const ACCEPT_LANGUAGE = new HttpContextToken<string | null>(() => null);

/**
 * Build (or extend) an `HttpContext` that tags a request with the v2 language,
 * e.g. `this.http.get(url, { context: withLanguage(lang) })`.
 */
export function withLanguage(lang: string, context: HttpContext = new HttpContext()): HttpContext {
  return context.set(ACCEPT_LANGUAGE, lang);
}

/**
 * Whether a request originates from the isolated /v2 surface — i.e. it was built
 * by `AppHttp`, which tags EVERY call with {@link ACCEPT_LANGUAGE} (default
 * `null` ⇒ not /v2). The GLOBAL v1 interceptors read this to EXEMPT /v2 traffic
 * from v1-specific handling, honouring the /v2 isolation rule:
 *   · v2 owns its OWN bearer (per request, from its namespaced token) — v1's
 *     `authInterceptor` must not overlay/clobber it, and
 *   · v2 owns its OWN error normalisation + toast (`AppHttp.toApiError`) — v1's
 *     `errorInterceptor` must not strip the real status/message nor bounce a
 *     v2 `401` to the dead v1 login route.
 * v1 requests never set the marker, so their behaviour is byte-for-byte unchanged.
 */
export function isPrimaryRequest(req: HttpRequest<unknown>): boolean {
  return req.context.get(ACCEPT_LANGUAGE) !== null;
}
